import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import V2AuthButton from "./components/V2AuthButton";

const decodeJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

export default function AcceptInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const workspaceParam = searchParams.get("workspace");

  const [status, setStatus] = useState("loading"); // loading | unauthenticated | accepting | wrong_user | error
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const currentUrl = `/v2/invite/accept?token=${encodeURIComponent(
    token || ""
  )}&workspace=${encodeURIComponent(workspaceParam || "")}`;

  const acceptInvite = useCallback(async () => {
    setStatus("accepting");
    try {
      await betaAPI.acceptInvite({ token });
      sessionStorage.removeItem("pendingInviteUrl");
      showToast.success(i18n.t("auth.acceptInvite.joinedToast"));
      const targetWorkspace = workspaceParam || payload?.workspace_id;
      navigate(`/v2/${targetWorkspace}`, { replace: true });
    } catch (err) {
      console.error("Accept invite error:", err);
      setError(err.message || i18n.t("auth.acceptInvite.acceptFailed"));
      setStatus("error");
    }
  }, [token, workspaceParam, payload, navigate]);

  useEffect(() => {
    if (!token) {
      setError(i18n.t("auth.acceptInvite.noToken"));
      setStatus("error");
      return;
    }

    const decoded = decodeJwtPayload(token);
    setPayload(decoded);

    const authToken = localStorage.getItem("authToken");
    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;

    if (!authToken || !user) {
      setStatus("unauthenticated");
      return;
    }

    setCurrentUser(user);

    if (
      decoded?.invitee_email &&
      user.email?.toLowerCase() !== decoded.invitee_email.toLowerCase()
    ) {
      setStatus("wrong_user");
      return;
    }

    // Correct user — auto-accept
    setStatus("accepting");
  }, [token]);

  // Trigger accept when status becomes "accepting"
  useEffect(() => {
    if (status === "accepting") {
      acceptInvite();
    }
  }, [status, acceptInvite]);

  const handleLogin = () => {
    sessionStorage.setItem("pendingInviteUrl", currentUrl);
    navigate(`/v2/auth/login?next=${encodeURIComponent(currentUrl)}`, {
      replace: true,
    });
  };

  const handleRegister = () => {
    sessionStorage.setItem("pendingInviteUrl", currentUrl);
    navigate("/v2/auth/register", { replace: true });
  };

  const handleSwitchAccount = async () => {
    sessionStorage.setItem("pendingInviteUrl", currentUrl);
    try {
      await betaAPI.logout();
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    navigate(`/v2/auth/login?next=${encodeURIComponent(currentUrl)}`, {
      replace: true,
    });
  };

  // Loading / accepting state
  if (status === "loading" || status === "accepting") {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#E07A3A]" />
        <p className="mt-4 text-sm text-[#6B7280]">
          {status === "accepting"
            ? t("auth.acceptInvite.accepting")
            : t("auth.acceptInvite.loadingDetails")}
        </p>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <svg
            className="h-6 w-6 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.acceptInvite.errorTitle")}
        </h1>
        <p className="mb-6 text-sm text-[#6B7280]">{error}</p>
        <V2AuthButton
          variant="secondary"
          onClick={() => navigate("/v2", { replace: true })}
        >
          {t("auth.common.goToDashboard")}
        </V2AuthButton>
      </div>
    );
  }

  // Not logged in
  if (status === "unauthenticated") {
    return (
      <div className="text-center">
        <div
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.15) 0%, rgba(214, 107, 46, 0.1) 100%)",
          }}
        >
          <svg
            className="h-7 w-7 text-[#E07A3A]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
        </div>

        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.acceptInvite.invitedTitle")}
        </h1>
        <p className="mb-1 text-sm text-[#6B7280]">
          {payload?.role ? (
            <Trans
              i18nKey="auth.acceptInvite.invitedToJoinAsRole"
              values={{ role: payload.role }}
              components={{
                role: <span className="font-medium text-[#374151]" />,
              }}
            />
          ) : (
            t("auth.acceptInvite.invitedToJoin")
          )}
        </p>
        {payload?.invitee_email && (
          <p className="mb-6 text-sm text-[#6B7280]">
            <Trans
              i18nKey="auth.acceptInvite.sentTo"
              values={{ email: payload.invitee_email }}
              components={{
                email: <span className="font-medium text-[#111827]" />,
              }}
            />
          </p>
        )}

        <div className="space-y-3">
          <V2AuthButton variant="primary" onClick={handleLogin}>
            {t("auth.acceptInvite.logIn")}
          </V2AuthButton>
          <V2AuthButton variant="secondary" onClick={handleRegister}>
            {t("auth.acceptInvite.createAccount")}
          </V2AuthButton>
        </div>
      </div>
    );
  }

  // Wrong user
  if (status === "wrong_user") {
    return (
      <div className="text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FEF3C7]">
          <svg
            className="h-7 w-7 text-[#D97706]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.acceptInvite.wrongAccountTitle")}
        </h1>
        <p className="mb-1 text-sm text-[#6B7280]">
          <Trans
            i18nKey="auth.acceptInvite.inviteFor"
            values={{ email: payload?.invitee_email }}
            components={{
              email: <span className="font-medium text-[#111827]" />,
            }}
          />
        </p>
        <p className="mb-6 text-sm text-[#6B7280]">
          <Trans
            i18nKey="auth.acceptInvite.signedInAs"
            values={{ email: currentUser?.email }}
            components={{
              email: <span className="font-medium text-[#111827]" />,
            }}
          />
        </p>

        <div className="space-y-3">
          <V2AuthButton variant="primary" onClick={handleSwitchAccount}>
            {t("auth.acceptInvite.switchAccount")}
          </V2AuthButton>
          <V2AuthButton
            variant="secondary"
            onClick={() => navigate("/v2", { replace: true })}
          >
            {t("auth.common.goToDashboard")}
          </V2AuthButton>
        </div>
      </div>
    );
  }

  return null;
}
